import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Image, Upload, Eye, RotateCcw, X, Save, Download, Copy } from 'lucide-react';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  padding: 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background: ${props => props.theme.background};
`;

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin: 0 0 12px 0;
`;

const ToggleSwitch = styled.button`
  width: 40px;
  height: 20px;
  border-radius: 10px;
  border: none;
  background: ${props => props.active ? props.theme.primary : props.theme.border};
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: white;
    top: 2px;
    left: ${props => props.active ? '22px' : '2px'};
    transition: all 0.2s ease;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  color: ${props => props.theme.text};
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.text};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => props.theme.border};
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.theme.surface};

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}10;
  }

  &.has-image {
    border-style: solid;
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}10;
  }
`;

const UploadIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.border};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  color: ${props => props.theme.textSecondary};
`;

const UploadText = styled.div`
  font-size: 14px;
  color: ${props => props.theme.text};
  margin-bottom: 4px;
`;

const UploadSubtext = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
`;

const ImagePreview = styled.div`
  position: relative;
  display: inline-block;
`;

const PreviewImage = styled.img`
  max-width: 100px;
  max-height: 100px;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.border};
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: ${props => props.theme.danger};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;

  &:hover {
    background: ${props => props.theme.danger}dd;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const RangeInput = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: ${props => props.theme.border};
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.primary};
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => props.theme.primary};
    cursor: pointer;
    border: none;
  }
`;

const RangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RangeValue = styled.span`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  min-width: 40px;
  text-align: right;
`;

const ColorInput = styled.input`
  width: 60px;
  height: 36px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  cursor: pointer;
  background: none;
  padding: 2px;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

const PreviewContainer = styled.div`
  margin-top: 16px;
`;

const PreviewTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.text};
  margin-bottom: 8px;
`;

const VideoPreview = styled.div`
  position: relative;
  width: 100%;
  height: 120px;
  background: linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #333 75%), linear-gradient(-45deg, transparent 75%, #333 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
`;

const WatermarkOverlay = styled.div`
  position: absolute;
  ${props => {
    // 🔧 FIX: Custom Position works regardless of dynamic movement
    if (props.customPosition?.enabled) {
      const x = props.customPosition.x || 50;
      const y = props.customPosition.y || 50;
      return `
        left: ${x}%;
        top: ${y}%;
        transform: translate(-50%, -50%);
      `;
    }
    
    switch (props.position) {
      case 'top-left':
        return 'top: 10px; left: 10px;';
      case 'top-center':
        return 'top: 10px; left: 50%; transform: translateX(-50%);';
      case 'top-right':
        return 'top: 10px; right: 10px;';
      case 'center-left':
        return 'top: 50%; left: 10px; transform: translateY(-50%);';
      case 'center-center':
        return 'top: 50%; left: 50%; transform: translate(-50%, -50%);';
      case 'center-right':
        return 'top: 50%; right: 10px; transform: translateY(-50%);';
      case 'bottom-left':
        return 'bottom: 10px; left: 10px;';
      case 'bottom-center':
        return 'bottom: 10px; left: 50%; transform: translateX(-50%);';
      case 'bottom-right':
      default:
        return 'bottom: 10px; right: 10px;';
    }
  }}
  
  opacity: ${props => props.opacity || 1};
  
  /* 🔧 FIX: Different styling for text vs image watermarks */
  ${props => props.watermarkType === 'text' ? `
    background: rgba(0, 0, 0, 0.7);
    color: ${props.textColor || 'white'};
    padding: 4px 8px;
    border-radius: 4px;
    font-family: ${props.font || 'Arial'};
    font-size: ${props => {
    switch (props.size) {
        case 'small': return '10px';
        case 'medium': return '12px';
        case 'large': return '16px';
        case 'xlarge': return '20px';
        default: return '12px';
      }
    }};
    font-weight: 500;
  ` : `
    /* Image watermark styling */
    max-width: ${props => {
      switch (props.size) {
        case 'small': return '60px';
        case 'medium': return '80px';
        case 'large': return '120px';
        case 'xlarge': return '160px';
        default: return '80px';
      }
    }};
    max-height: ${props => {
      switch (props.size) {
        case 'small': return '60px';
        case 'medium': return '80px';
        case 'large': return '120px';
        case 'xlarge': return '160px';
        default: return '80px';
      }
    }};
  `}
  
  z-index: 10;
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.border};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.primary}dd;
  }

  &:disabled {
    background: ${props => props.theme.border};
    cursor: not-allowed;
  }
`;

const PresetItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: ${props => props.theme.surface};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  transition: all 0.2s ease;
  margin-bottom: 8px;

  &:hover {
    border-color: ${props => props.theme.primary};
    background: ${props => props.theme.primary}05;
  }
`;

const PresetInfo = styled.div`
  flex: 1;
`;

const PresetName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 2px;
`;

const PresetDetails = styled.div`
  font-size: 11px;
  color: ${props => props.theme.textSecondary};
`;

const PresetActions = styled.div`
  display: flex;
  gap: 6px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.background};
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid ${props => props.theme.border};

  h3 {
    margin: 0;
  font-size: 16px;
  color: ${props => props.theme.text};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: ${props => props.theme.border};
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.text};
  margin-bottom: 6px;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid ${props => props.theme.border};
`;

const ModalButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;

  &:hover {
    background: ${props => props.theme.border};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WatermarkSettings = ({ project, onUpdate }) => {
  const watermarkSettings = project.settings?.watermark || {
    enabled: false,
    type: 'text',
    text: 'My Logo',
    font: 'Arial',
    fontSize: 24,
    textColor: '#ffffff',
    path: null,
    position: "bottom-right",
    // 🆕 NEW: Custom Position Support
    customPosition: {
      enabled: false,
      x: 85,
      y: 85
    },
    opacity: 1.0,
    size: "medium",
    dynamicMovement: true,
    movementSpeed: 'medium',
    movementPattern: "random"
  };

  const [settings, setSettings] = useState(watermarkSettings);
  const [previewImage, setPreviewImage] = useState(null);
  
  // 🆕 NEW: Multiple Preset Management
  const [savedWatermarkPresets, setSavedWatermarkPresets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Load saved watermark presets on component mount
  useEffect(() => {
    const loadWatermarkPresets = () => {
      try {
        const saved = localStorage.getItem('watermarkPresets');
        console.log('🔍 Loading watermark presets from localStorage:', saved);
        
        if (saved && saved !== 'null' && saved !== 'undefined') {
          const parsed = JSON.parse(saved);
          console.log('🔍 Parsed watermark presets:', parsed);
          console.log('🔍 Number of presets found:', parsed.length);
          
          // Validate that parsed is an array
          if (Array.isArray(parsed)) {
            setSavedWatermarkPresets(parsed);
            console.log('✅ Successfully loaded', parsed.length, 'watermark presets');
          } else {
            console.warn('⚠️ Invalid presets format, resetting to empty array');
            setSavedWatermarkPresets([]);
            localStorage.removeItem('watermarkPresets');
          }
        } else {
          console.log('🔍 No watermark presets found in localStorage');
          setSavedWatermarkPresets([]);
        }
      } catch (error) {
        console.error('❌ Error loading watermark presets:', error);
        console.error('❌ Resetting presets due to error');
        setSavedWatermarkPresets([]);
        localStorage.removeItem('watermarkPresets');
      }
    };
    
    loadWatermarkPresets();
    
    // 🔧 FIX: Re-check localStorage after a short delay to ensure it's loaded
    const recheckTimer = setTimeout(() => {
      console.log('🔄 Re-checking localStorage after delay...');
      loadWatermarkPresets();
    }, 500);
    
    return () => clearTimeout(recheckTimer);
  }, []);

  // Load initial image preview if watermark has an image path
  useEffect(() => {
    const loadInitialImagePreview = async () => {
      if (settings.type === 'image' && settings.path) {
        if (window.require && !settings.path.startsWith('data:')) {
          try {
            const { ipcRenderer } = window.require('electron');
            const response = await ipcRenderer.invoke('file-to-data-url', settings.path);
            if (response.success) {
              setPreviewImage(response.dataUrl);
              console.log('🖼️ Initial image preview loaded successfully');
            } else {
              console.error('🖼️ Error loading initial image preview:', response.error);
              setPreviewImage(null);
            }
          } catch (error) {
            console.error('🖼️ Error loading initial image preview:', error);
            setPreviewImage(null);
          }
        } else if (!window.require && settings.path && !settings.path.startsWith('data:')) {
          // Web mode: use LocalAgent for absolute paths
          const previewUrl = `http://localhost:8001/get-watermark?path=${encodeURIComponent(settings.path)}`;
          setPreviewImage(previewUrl);
        } else {
          setPreviewImage(settings.path);
        }
      } else {
        setPreviewImage(null);
      }
    };
    
    loadInitialImagePreview();
  }, [settings.type, settings.path]);

  const positions = [
    { value: 'top-left', label: 'Top Left' },
    { value: 'top-center', label: 'Top Center' },
    { value: 'top-right', label: 'Top Right' },
    { value: 'center-left', label: 'Center Left' },
    { value: 'center-center', label: 'Center' },
    { value: 'center-right', label: 'Center Right' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'bottom-center', label: 'Bottom Center' },
    { value: 'bottom-right', label: 'Bottom Right' }
  ];

  const sizes = [
    { value: 'small', label: 'Small (15%)' },
    { value: 'medium', label: 'Medium (25%)' },
    { value: 'large', label: 'Large (35%)' },
    { value: 'xlarge', label: 'Extra Large (50%)' }
  ];

  const fonts = [
    'Arial', 'Helvetica', 'Times New Roman', 'Georgia', 'Verdana',
    'Trebuchet MS', 'Impact', 'Comic Sans MS', 'Courier New', 'Lucida Console'
  ];

  const movementSpeeds = [
    { value: 'slow', label: 'Slow' },
    { value: 'medium', label: 'Medium' },
    { value: 'fast', label: 'Fast' }
  ];

  const movementPatterns = [
    { value: 'random', label: 'Random Movement' },
    { value: 'circular', label: 'Circular Motion' },
    { value: 'horizontal', label: 'Horizontal Slide' },
    { value: 'vertical', label: 'Vertical Slide' },
    { value: 'corners', label: 'Corner to Corner' }
  ];

  const updateSettings = (updates) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    
    const updatedProjectSettings = {
      ...project.settings,
      watermark: newSettings
    };
    onUpdate({ settings: updatedProjectSettings });
  };

  const handleImageUpload = async () => {
    if (window.require) {
      const { ipcRenderer } = window.require('electron');
      try {
        const filePath = await ipcRenderer.invoke('select-file', {
          filters: [
            { name: 'Image Files', extensions: ['png', 'jpg', 'jpeg', 'gif', 'svg'] }
          ]
        });
        
        if (filePath) {
          const response = await ipcRenderer.invoke('file-to-data-url', filePath);
          if (response.success) {
            setPreviewImage(response.dataUrl);
            updateSettings({ path: filePath });
            console.log('🖼️ Image uploaded successfully:', filePath);
          } else {
            console.error('🖼️ Error converting image to data URL:', response.error);
            alert('❌ Failed to process image. Please try again.');
          }
        }
      } catch (error) {
        console.error('🖼️ Error uploading image:', error);
        alert('❌ Failed to upload image. Please try again.');
      }
    } else {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
          const dataUrl = event.target.result;
          // Show preview immediately
          setPreviewImage(dataUrl);

          // Try to upload to LocalAgent for a permanent path usable by FFmpeg
          try {
            const res = await fetch('http://localhost:8001/save-watermark-image', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ dataUrl, filename: file.name })
            });
            const json = await res.json();
            if (json.success && json.path) {
              // Store the file path — FFmpeg + presets can use this!
              const previewUrl = `http://localhost:8001/get-watermark?path=${encodeURIComponent(json.path)}`;
              setPreviewImage(previewUrl);
              updateSettings({ path: json.path, _previewDataUrl: dataUrl });
              console.log('🖼️ Watermark image saved to LocalAgent:', json.path);
            } else {
              // Fallback: keep data: URL (preview works, export may fail)
              updateSettings({ path: dataUrl });
              console.warn('🖼️ LocalAgent did not return a path, using data: URL');
            }
          } catch (err) {
            // LocalAgent not running — keep data: URL as fallback
            updateSettings({ path: dataUrl });
            console.warn('🖼️ LocalAgent unavailable, using data: URL:', err.message);
          }
        };
        reader.readAsDataURL(file);
      };
      input.click();
    }
  };

  const handleRemoveImage = () => {
    setPreviewImage(null);
    updateSettings({ path: null });
  };

  const resetToDefaults = () => {
    const defaultSettings = {
      enabled: false,
      type: 'text',
      text: 'My Logo',
      font: 'Arial',
      fontSize: 24,
      textColor: '#ffffff',
      path: null,
      position: "bottom-right",
      customPosition: {
        enabled: false,
        x: 85,
        y: 85
      },
      opacity: 1.0,
      size: "medium",
      dynamicMovement: true,
      movementSpeed: 'medium',
      movementPattern: "random"
    };
    setSettings(defaultSettings);
    setPreviewImage(null);
    updateSettings(defaultSettings);
  };

  // Save watermark preset — strip large data: URLs to avoid localStorage quota errors
  const saveWatermarkPreset = () => {
    if (!presetName.trim()) return;
    
    // Strip large data: URL from image path before saving, but KEEP permanent file paths
    const settingsToSave = { ...settings };
    let stripped = false;

    if (settingsToSave.path && settingsToSave.path.startsWith('data:')) {
      settingsToSave.path = null; 
      stripped = true;
    }

    const newPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      watermarkType: settings.type,
      settings: settingsToSave,
      createdAt: new Date().toISOString()
    };

    try {
      const updatedPresets = [...savedWatermarkPresets, newPreset];
      localStorage.setItem('watermarkPresets', JSON.stringify(updatedPresets));
      
      setSavedWatermarkPresets(updatedPresets);
      setShowSaveModal(false);
      setPresetName('');
      
      if (stripped) {
        alert(`✅ Preset "${newPreset.name}" saved!\n⚠️ Note: Image file was too large to store in preset. You'll need to re-upload it when loading this preset.`);
      } else {
        alert(`✅ Preset "${newPreset.name}" saved successfully!`);
      }
    } catch (error) {
      console.error('❌ Error saving preset:', error);
      alert(error.name === 'QuotaExceededError' ? '❌ Storage full!' : '❌ Failed to save preset.');
    }
  };


  // 🆕 NEW: Clear all presets function
  const clearAllPresets = () => {
    if (window.confirm('🗑️ Are you sure you want to delete ALL watermark presets?\n\nThis action cannot be undone.')) {
      try {
        localStorage.removeItem('watermarkPresets');
        setSavedWatermarkPresets([]);
        console.log('🗑️ All watermark presets cleared');
        alert('✅ All presets have been cleared!');
      } catch (error) {
        console.error('❌ Error clearing presets:', error);
        alert('❌ Failed to clear presets. Please try again.');
      }
    }
  };

  // 🆕 NEW: Force reload presets function
  const forceReloadPresets = () => {
    try {
      const saved = localStorage.getItem('watermarkPresets');
      console.log('🔄 Force reloading presets:', saved);
      
      if (saved && saved !== 'null' && saved !== 'undefined') {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setSavedWatermarkPresets(parsed);
          alert(`🔄 Reloaded ${parsed.length} presets from localStorage`);
        } else {
          setSavedWatermarkPresets([]);
          alert('⚠️ Invalid presets format found');
        }
      } else {
        setSavedWatermarkPresets([]);
        alert('📭 No presets found in localStorage');
      }
    } catch (error) {
      console.error('❌ Error force reloading presets:', error);
      alert('❌ Error reloading presets: ' + error.message);
    }
  };

  // Load watermark preset
  const loadWatermarkPreset = async (preset) => {
    console.log('📂 Loading watermark preset:', preset.name);
    
    const presetSettings = preset.settings;
    setSettings(presetSettings);
    
    // Handle image preview for loaded presets
    if (presetSettings.type === 'image') {
      const imgPath = presetSettings.path;
      const previewDataUrl = presetSettings._previewDataUrl;
      
      if (previewDataUrl) {
        // We stored a preview data URL — use it directly
        setPreviewImage(previewDataUrl);
      } else if (imgPath && imgPath.startsWith('data:')) {
        // Raw data URL stored — use as preview
        setPreviewImage(imgPath);
      } else if (imgPath && !imgPath.startsWith('data:')) {
        // File path — fetch from LocalAgent for browser preview
        const previewUrl = `http://localhost:8001/get-watermark?path=${encodeURIComponent(imgPath)}`;
        setPreviewImage(previewUrl);
        console.log('🖼️ Loading preview from LocalAgent:', imgPath);
      } else {
        setPreviewImage(null);
      }
    } else {
      setPreviewImage(null);
    }
    
    updateSettings(presetSettings);
  };

  // 🆕 NEW: Delete watermark preset
  const deleteWatermarkPreset = (presetId) => {
    const updatedPresets = savedWatermarkPresets.filter(p => p.id !== presetId);
    setSavedWatermarkPresets(updatedPresets);
    localStorage.setItem('watermarkPresets', JSON.stringify(updatedPresets));
    console.log('🗑️ Watermark preset deleted');
  };

  return (
    <Container>
      <Section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <SectionTitle style={{ margin: 0 }}>Watermark</SectionTitle>
          <ResetButton onClick={resetToDefaults}>
            <RotateCcw size={12} />
            Reset
          </ResetButton>
        </div>
        
        <ToggleContainer>
          <ToggleSwitch
            active={settings.enabled}
            onClick={() => updateSettings({ enabled: !settings.enabled })}
          />
          <ToggleLabel>
            {settings.enabled ? 'Enabled' : 'Disabled'}
          </ToggleLabel>
        </ToggleContainer>

        {settings.enabled && (
          <>
            <FormGroup>
              <Label>📝 Watermark Type</Label>
              <Select
                value={settings.type}
                onChange={(e) => updateSettings({ type: e.target.value })}
              >
                <option value="text">✍️ Text Watermark (Custom Text)</option>
                <option value="image">🖼️ Image Watermark (Upload Logo)</option>
              </Select>
            </FormGroup>

            {settings.type === 'text' ? (
              <div style={{ 
                padding: '12px', 
                background: '#f0f8ff', 
                borderRadius: '8px', 
                border: '2px dashed #6f42c1',
                marginBottom: '16px'
              }}>
                <FormGroup>
                  <Label>💬 Watermark Text</Label>
                  <Input
                    type="text"
                    value={settings.text}
                    onChange={(e) => updateSettings({ text: e.target.value })}
                    placeholder="Enter your watermark text here..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>🎨 Font Family</Label>
                  <Select
                    value={settings.font}
                    onChange={(e) => updateSettings({ font: e.target.value })}
                  >
                    {fonts.map(font => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>📏 Font Size</Label>
                  <RangeContainer>
                    <RangeInput
                      type="range"
                      min="12"
                      max="48"
                      value={settings.fontSize}
                      onChange={(e) => updateSettings({ fontSize: parseInt(e.target.value) })}
                    />
                    <RangeValue>{settings.fontSize}px</RangeValue>
                  </RangeContainer>
                </FormGroup>

                <FormGroup>
                  <Label>🎨 Text Color</Label>
                  <ColorInput
                    type="color"
                    value={settings.textColor}
                    onChange={(e) => updateSettings({ textColor: e.target.value })}
                  />
                </FormGroup>
              </div>
            ) : (
              <div style={{ 
                padding: '12px', 
                background: '#f0fff0', 
                borderRadius: '8px', 
                border: '2px dashed #28a745',
                marginBottom: '16px'
              }}>
                <FormGroup>
                  <Label>📁 Upload Logo/Image</Label>
              <UploadArea 
                className={previewImage ? 'has-image' : ''}
                onClick={handleImageUpload}
              >
                {previewImage ? (
                  <ImagePreview>
                    <PreviewImage 
                      src={previewImage} 
                      alt="Watermark preview" 
                      onError={(e) => {
                        console.error('🖼️ Preview image failed to load:', previewImage);
                        // If it failed and it's a localhost URL, maybe try raw path as last resort (though browser might block)
                        if (previewImage.includes('localhost:8001') && settings.path && !settings.path.startsWith('data:')) {
                           // Try without encodeURIComponent just in case, or different port? 
                           // But usually it's better to just log it for now.
                        }
                      }}
                    />
                    <RemoveButton onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}>
                      <X size={12} />
                    </RemoveButton>
                  </ImagePreview>
                ) : (
                  <>
                    <UploadIcon>
                      <Upload size={20} />
                    </UploadIcon>
                        <UploadText>Click to upload image</UploadText>
                    <UploadSubtext>PNG, JPG, GIF, SVG up to 10MB</UploadSubtext>
                  </>
                )}
              </UploadArea>
            </FormGroup>
              </div>
            )}

            {/* ── Position Mode: exclusive radio group ── */}
            <FormGroup>
              <Label>📍 Position Mode</Label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                {[
                  { value: 'preset',  label: '📍 Preset Position',            desc: 'Choose from 9 fixed positions' },
                  { value: 'custom',  label: '🎯 Custom Position (X/Y)',       desc: 'Set exact % coordinates' },
                  { value: 'dynamic', label: '🔄 Dynamic Movement',            desc: 'Watermark moves around video' },
                ].map(opt => {
                  const currentMode = settings.positionMode ||
                    (settings.dynamicMovement ? 'dynamic' :
                     settings.customPosition?.enabled ? 'custom' : 'preset');
                  const isActive = currentMode === opt.value;
                  return (
                    <div
                      key={opt.value}
                      onClick={() => updateSettings({
                        positionMode: opt.value,
                        // keep backwards compat flags in sync
                        dynamicMovement: opt.value === 'dynamic',
                        customPosition: {
                          ...settings.customPosition,
                          enabled: opt.value === 'custom',
                        },
                      })}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: `2px solid ${isActive ? '#007bff' : '#dee2e6'}`,
                        background: isActive ? '#007bff10' : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '50%',
                        border: `2px solid ${isActive ? '#007bff' : '#adb5bd'}`,
                        background: isActive ? '#007bff' : 'transparent',
                        flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {isActive && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white' }} />}
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: isActive ? '600' : '400', color: isActive ? '#007bff' : 'inherit' }}>
                          {opt.label}
                        </div>
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '1px' }}>{opt.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </FormGroup>

            {/* ── Preset Position parameters ── */}
            {(settings.positionMode === 'preset' || (!settings.positionMode && !settings.dynamicMovement && !settings.customPosition?.enabled)) && (
              <FormGroup>
                <Label>📍 Position</Label>
                <Select
                  value={settings.position}
                  onChange={(e) => updateSettings({ position: e.target.value })}
                >
                  {positions.map(pos => (
                    <option key={pos.value} value={pos.value}>{pos.label}</option>
                  ))}
                </Select>
              </FormGroup>
            )}

            {/* ── Custom Position parameters ── */}
            {(settings.positionMode === 'custom' || (!settings.positionMode && settings.customPosition?.enabled)) && (
              <FormGroup>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <Label style={{ fontSize: '12px' }}>X Position (%)</Label>
                    <RangeContainer>
                      <RangeInput
                        type="range" min="5" max="95"
                        value={settings.customPosition?.x || 85}
                        onChange={(e) => updateSettings({ customPosition: { ...settings.customPosition, x: parseInt(e.target.value) } })}
                      />
                      <RangeValue>{settings.customPosition?.x || 85}%</RangeValue>
                    </RangeContainer>
                  </div>
                  <div>
                    <Label style={{ fontSize: '12px' }}>Y Position (%)</Label>
                    <RangeContainer>
                      <RangeInput
                        type="range" min="5" max="95"
                        value={settings.customPosition?.y || 85}
                        onChange={(e) => updateSettings({ customPosition: { ...settings.customPosition, y: parseInt(e.target.value) } })}
                      />
                      <RangeValue>{settings.customPosition?.y || 85}%</RangeValue>
                    </RangeContainer>
                  </div>
                </div>
              </FormGroup>
            )}

            {/* ── Dynamic Movement parameters ── */}
            {(settings.positionMode === 'dynamic' || (!settings.positionMode && settings.dynamicMovement)) && (
              <>
                <FormGroup>
                  <Label>Movement Pattern</Label>
                  <Select
                    value={settings.movementPattern}
                    onChange={(e) => updateSettings({ movementPattern: e.target.value })}
                  >
                    {movementPatterns.map(pattern => (
                      <option key={pattern.value} value={pattern.value}>{pattern.label}</option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup>
                  <Label>Movement Speed</Label>
                  <Select
                    value={settings.movementSpeed}
                    onChange={(e) => updateSettings({ movementSpeed: e.target.value })}
                  >
                    {movementSpeeds.map(speed => (
                      <option key={speed.value} value={speed.value}>{speed.label}</option>
                    ))}
                  </Select>
                </FormGroup>
              </>
            )}

            <FormGroup>
              <Label>📏 Size</Label>
              <Select value={settings.size} onChange={(e) => updateSettings({ size: e.target.value })}>
                {sizes.map(size => <option key={size.value} value={size.value}>{size.label}</option>)}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>👻 Opacity</Label>
              <RangeContainer>
                <RangeInput
                  type="range" min="0.1" max="1" step="0.1"
                  value={settings.opacity}
                  onChange={(e) => updateSettings({ opacity: parseFloat(e.target.value) })}
                />
                <RangeValue>{Math.round(settings.opacity * 100)}%</RangeValue>
              </RangeContainer>
            </FormGroup>
          </>
        )}
      </Section>




      {/* 🆕 NEW: Enhanced Watermark Presets Section */}
      <Section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <SectionTitle>Watermark Presets ({savedWatermarkPresets.length})</SectionTitle>
          <div style={{ display: 'flex', gap: '8px' }}>
            <SaveButton
            onClick={() => setShowSaveModal(true)}
            disabled={!settings.enabled}
          >
            <Save size={14} />
              Save Preset
            </SaveButton>
          {savedWatermarkPresets.length > 0 && (
              <SaveButton
                onClick={clearAllPresets}
                style={{ background: '#dc3545' }}
              >
                🗑️ Clear All
              </SaveButton>
            )}
            <SaveButton
              onClick={forceReloadPresets}
              style={{ background: '#28a745' }}
            >
              🔄 Reload
            </SaveButton>
          </div>
        </div>

        {savedWatermarkPresets.length > 0 ? (
          <div>
            {console.log('🔍 Rendering presets. Current state:', savedWatermarkPresets)}
            {savedWatermarkPresets.map((preset, index) => {
              console.log(`🔍 Rendering preset ${index + 1}:`, preset);
              return (
              <PresetItem key={preset.id}>
                <PresetInfo>
                    <PresetName>{preset.name}</PresetName>
                    <PresetDetails>
                      {preset.watermarkType === 'text' ? '✍️ Text' : '🖼️ Image'} • 
                      Created {new Date(preset.createdAt).toLocaleDateString()}
                    </PresetDetails>
                </PresetInfo>
                  <PresetActions>
                    <ActionButton onClick={() => loadWatermarkPreset(preset)}>
                      <Download size={12} />
                      Load
                    </ActionButton>
                    <ActionButton onClick={() => deleteWatermarkPreset(preset.id)}>
                    <X size={12} />
                    </ActionButton>
                  </PresetActions>
              </PresetItem>
              );
            })}
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            color: '#6c757d', 
            fontSize: '14px',
            padding: '20px'
          }}>
            <div style={{ marginBottom: '15px' }}>
              📭 No saved presets yet. Configure your watermark and save it as a preset!
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <button 
                onClick={() => {
                  console.log('🔍 DEBUG: localStorage content:', localStorage.getItem('watermarkPresets'));
                  const saved = localStorage.getItem('watermarkPresets');
                  if (saved) {
                    try {
                      const parsed = JSON.parse(saved);
                      console.log('🔍 DEBUG: Parsed content:', parsed);
                      alert(`Found ${parsed.length} presets in localStorage:\n${parsed.map(p => p.name).join('\n')}\n\nCheck console for details.`);
                    } catch (e) {
                      alert('Error parsing localStorage: ' + e.message);
                    }
                  } else {
                    alert('No watermarkPresets found in localStorage');
                  }
                }}
                style={{ 
                  padding: '6px 12px', 
                  fontSize: '12px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🔍 Debug localStorage
              </button>
              
              <button 
                onClick={forceReloadPresets}
                style={{ 
                  padding: '6px 12px', 
                  fontSize: '12px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🔄 Force Reload
              </button>
              
              <button 
                onClick={() => {
                  localStorage.removeItem('watermarkPresets');
                  setSavedWatermarkPresets([]);
                  alert('🧹 localStorage cleared!');
                }}
                style={{ 
                  padding: '6px 12px', 
                  fontSize: '12px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🧹 Clear Storage
              </button>
            </div>
          </div>
        )}
      </Section>

      {/* Save Preset Modal */}
      {showSaveModal && (
        <ModalOverlay onClick={() => setShowSaveModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Save Watermark Preset</h3>
              <CloseButton onClick={() => setShowSaveModal(false)}>
                <X size={16} />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <ModalLabel>Preset Name</ModalLabel>
            <ModalInput
              type="text"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Enter preset name..."
              autoFocus
            />
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={() => setShowSaveModal(false)}>
                Cancel
              </ModalButton>
              <ModalButton 
                onClick={saveWatermarkPreset}
                disabled={!presetName.trim()}
                style={{ background: '#007bff', color: 'white' }}
              >
                Save Preset
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default WatermarkSettings;